/**
 * Affiliate review statuses.
 *
 * The affiliate controller moved off the integer `isActive` it used to report:
 * reads now answer `statusId` + `statusName` beside a boolean `isActive`, and
 * `AdminReviewAffiliate` writes `statusId` instead of `isActive`.
 *
 * The write vocabulary is published on the endpoint itself — "Admin accept
 * (StatusId=2) or reject (StatusId=3) / suspend (StatusId=4)" — and 1 is the
 * state a registration lands in ("Registers a new affiliate application
 * (Pending admin approval)").
 *
 * `deleted` is the one value the API does not honour yet. It is listed here so
 * the control panel's Deleted filter is wired end to end now and starts working
 * the moment the server recognises the id, with no client change. Sending an id
 * the server does not know is safe: unknown filter values narrow to nothing
 * rather than erroring, and deletion also keeps being reported as `isDeleted`
 * so a deleted row is still labelled correctly in every other listing.
 *
 * Resolution is deliberately layered. An id this build does not know must not
 * be rendered as a decision nobody took, so `statusName` is consulted next and
 * the legacy integer `isActive` last — `AffiliateValidateLogin` still reports
 * that one as an integer, so both shapes stay readable.
 */
import type {
  AffiliateReviewState,
  AffiliateStatusFilter,
} from './affiliateTypes';

export const AFFILIATE_STATUS_IDS = {
  /** Inferred from the register endpoint, not published as a number. */
  pending: 1,
  /** Published: "Admin accept (StatusId=2)". */
  approved: 2,
  /**
   * Published: "reject (StatusId=3)". This is Rejected, not Deleted — the two
   * are different states and 3 is also what the Reject action writes, so
   * pointing `deleted` at it would make rejecting an applicant delete them.
   */
  rejected: 3,
  /** Published: "suspend (StatusId=4)". */
  suspended: 4,
  /**
   * Placeholder: the API has not published a Deleted status id yet, and 5 is
   * only the next free number. Confirm with the API team before relying on the
   * Deleted filter; this line is the only edit when the real number lands.
   */
  deleted: 5,
} as const satisfies Record<AffiliateStatusFilter, number>;

/** Review decisions an operator can take; each maps to one `statusId`. */
export type AffiliateReviewDecision = 'approve' | 'reject' | 'suspend';

export const AFFILIATE_REVIEW_DECISION_STATUS_IDS: Record<
  AffiliateReviewDecision,
  number
> = {
  approve: AFFILIATE_STATUS_IDS.approved,
  reject: AFFILIATE_STATUS_IDS.rejected,
  suspend: AFFILIATE_STATUS_IDS.suspended,
};

/** The state each decision leaves the account in, for optimistic copy. */
export const AFFILIATE_REVIEW_DECISION_STATES: Record<
  AffiliateReviewDecision,
  AffiliateReviewState
> = {
  approve: 'approved',
  reject: 'rejected',
  suspend: 'suspended',
};

/**
 * Review decisions only. `deleted` is deliberately absent: it is not a decision
 * an operator took, and a deleted account still has to report the decision it
 * had so the rest of the row reads correctly.
 */
const STATE_BY_ID = new Map<number, AffiliateReviewState>([
  [AFFILIATE_STATUS_IDS.pending, 'pending'],
  [AFFILIATE_STATUS_IDS.approved, 'approved'],
  [AFFILIATE_STATUS_IDS.rejected, 'rejected'],
  [AFFILIATE_STATUS_IDS.suspended, 'suspended'],
]);

/**
 * `statusName` as the API spells it. Only used when the id is unrecognised, so
 * a deployment that renumbers the table still reads correctly; the synonyms are
 * the names the same state goes by elsewhere in the API ("Active" for an
 * approved affiliate, which is what the login endpoint calls it).
 */
const STATE_BY_NAME = new Map<string, AffiliateReviewState>([
  ['pending', 'pending'],
  ['pendingreview', 'pending'],
  ['awaitingreview', 'pending'],
  ['approved', 'approved'],
  ['active', 'approved'],
  ['rejected', 'rejected'],
  ['declined', 'rejected'],
  ['suspended', 'suspended'],
]);

export interface AffiliateStatusSource {
  statusId?: number | null;
  statusName?: string | null;
  /** Boolean on the read schemas; still an integer on `AffiliateValidateLogin`. */
  isActive?: boolean | number | null;
}

/**
 * The review state of an affiliate account.
 *
 * Falls back to `pending` rather than to a decision: telling an operator that
 * an application they have not reviewed was rejected is worse than showing it
 * as still waiting, and it keeps the approve action available.
 */
export const affiliateReviewState = (
  source: AffiliateStatusSource,
): AffiliateReviewState => {
  if (typeof source.statusId === 'number') {
    const byId = STATE_BY_ID.get(source.statusId);
    if (byId) return byId;
  }

  if (typeof source.statusName === 'string') {
    const byName = STATE_BY_NAME.get(
      source.statusName.toLowerCase().replace(/[\s_-]/g, ''),
    );
    if (byName) return byName;
  }

  if (typeof source.isActive === 'boolean') {
    return source.isActive ? 'approved' : 'pending';
  }

  // The integer the login endpoint still answers: 1 approved, 0 rejected,
  // -1 awaiting review.
  if (source.isActive === 1) return 'approved';
  if (source.isActive === 0) return 'rejected';
  return 'pending';
};

export const affiliateStatusIdFromState = (
  state: AffiliateStatusFilter,
): number => AFFILIATE_STATUS_IDS[state];

/**
 * Whether a record reads as deleted.
 *
 * `isDeleted` is the flag the API reports today. The `statusId` check covers the
 * Deleted status it is adding, so a row that arrives carrying only the new id is
 * still labelled closed instead of being shown as a live application.
 */
export const affiliateIsDeleted = (source: AffiliateStatusSource & {
  isDeleted?: boolean | null;
}): boolean =>
  source.isDeleted === true || source.statusId === AFFILIATE_STATUS_IDS.deleted;
